import { useEffect, useState } from "react";
import Button from "../ui/Button.jsx";
import ProviderFrameworkPicker, { normalizeFrameworksForProviderSubmit } from "./ProviderFrameworkPicker.jsx";
import { getDefaultProviderComplianceFrameworks } from "../../utils/providerFrameworks.js";
import { ORY_SELF_HOSTED_BODY_GUIDANCE } from "./orySelfHostedConnectGuidance.js";
import OryAttestationGate from "../ory/OryAttestationGate.jsx";

/** Live Ory modules with config-upload slots (Phase A1 + A2 Keto). */
export const CONFIG_UPLOAD_SLOTS = [
  {
    id: "kratos",
    label: "Kratos config — kratos.yml as JSON",
    placeholder: '{"selfservice": { "methods": { "password": { ... } } }, "session": { ... }}',
  },
  {
    id: "hydra",
    label: "Hydra config — hydra.yml as JSON",
    placeholder: '{"oauth2": { "pkce": { "enforced": true } }, "ttl": { "access_token": "1h" }, "urls": { ... }}',
  },
  {
    id: "keto",
    label: "Keto config — keto.yml as JSON (Tier A)",
    placeholder: '{"serve": { "read": { "host": "0.0.0.0", "port": 4466 }, "write": { "host": "127.0.0.1", "port": 4467 } }, "limit": { "max_read_depth": 8 }}',
  },
  {
    id: "oathkeeper",
    label: "Oathkeeper config — oathkeeper.yml as JSON (Tier A)",
    placeholder: '{"serve": { "proxy": { "host": "0.0.0.0", "port": 4455 }, "api": { "host": "127.0.0.1", "port": 4456 } }, "access_rules": { "repositories": [{ "url": "file://./rules.json" }] }}',
  },
  {
    id: "polis",
    label: "Polis config — env vars as JSON (Tier A)",
    placeholder: '{"EXTERNAL_URL": "https://sso.example.com", "HOST": "127.0.0.1", "PORT": 5225, "DB_TYPE": "postgres", "JACKSON_API_KEYS": ["[redacted-on-export]"]}',
  },
];

export const OATHKEEPER_SUPPLEMENTAL_SLOTS = [
  {
    id: "access_rules_upload",
    label: "Oathkeeper access rules — rules.json array (Tier B)",
    placeholder: '[{"id":"public","match":{"url":"<http://api.example.com/public>"},"authenticators":[{"handler":"noop"}],"authorizer":{"handler":"allow"},"mutators":[{"handler":"noop"}]}]',
    rows: 6,
  },
];

export const SUPPLEMENTAL_UPLOAD_SLOTS = [
  {
    id: "opl_upload",
    label: "Keto OPL namespace definitions — .keto.ts or namespace defs (Tier B)",
    placeholder: "class Document implements Namespace { related: { viewers: SubjectSet<...> }; permits = { view: (ctx) => ... }; }",
    rows: 6,
  },
  {
    id: "tuple_sample",
    label: "Keto relation-tuple sample — JSON array (Tier C, not stored raw)",
    placeholder: '[{"namespace":"Document","object":"readme","relation":"owner","subject_id":"user:alice"}]',
    rows: 5,
  },
];

export const POLIS_SUPPLEMENTAL_SLOTS = [
  {
    id: "sso_connections_upload",
    label: "Polis SSO connections — SAML/OIDC export JSON (Tier B, aggregates only)",
    placeholder: '[{"name":"acme","tenant":"acme.com","product":"sso","signRequest":false,"redirectUrl":"https://app.example.com/*"}]',
    rows: 6,
  },
  {
    id: "dsync_upload",
    label: "Polis SCIM/dsync — directory sync export JSON (Tier C, aggregates only)",
    placeholder: '{"product":"dsync","deprovisionStrategy":"none","scim":{"token":"short"}}',
    rows: 5,
  },
];

export const ALL_SUPPLEMENTAL_UPLOAD_SLOTS = [
  ...SUPPLEMENTAL_UPLOAD_SLOTS,
  ...OATHKEEPER_SUPPLEMENTAL_SLOTS,
  ...POLIS_SUPPLEMENTAL_SLOTS,
];

/**
 * Ory self-hosted connect panel — create or edit mode.
 *
 * @param {{
 *   mode?: 'create' | 'edit',
 *   projectId?: string,
 *   onBack?: () => void,
 *   onConnected?: (projectId: string) => void,
 *   onSaved?: (result: { attestation_stale?: boolean, input_change_summary?: string[] }) => void,
 *   compact?: boolean,
 * }} props
 */
export default function ConnectOrySelfHostedPanel({
  mode = "create",
  projectId,
  onBack,
  onConnected,
  onSaved,
  compact = false,
}) {
  const isEdit = mode === "edit";
  const [inventory, setInventory] = useState(null);
  const [inventoryBusy, setInventoryBusy] = useState(isEdit);
  const [kratosUrl, setKratosUrl] = useState("");
  const [hydraUrl, setHydraUrl] = useState("");
  const [authType, setAuthType] = useState("none");
  const [bearerToken, setBearerToken] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [configUploads, setConfigUploads] = useState({ kratos: "", hydra: "", keto: "", oathkeeper: "", polis: "" });
  const [configRemoves, setConfigRemoves] = useState({ kratos: false, hydra: false, keto: false, oathkeeper: false, polis: false });
  const [supplementalUploads, setSupplementalUploads] = useState({
    opl_upload: "",
    tuple_sample: "",
    access_rules_upload: "",
    sso_connections_upload: "",
    dsync_upload: "",
  });
  const [supplementalRemoves, setSupplementalRemoves] = useState({
    opl_upload: false,
    tuple_sample: false,
    access_rules_upload: false,
    sso_connections_upload: false,
    dsync_upload: false,
  });
  const [testOk, setTestOk] = useState(false);
  const [components, setComponents] = useState(null);
  const [testBusy, setTestBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [frameworks, setFrameworks] = useState(() => getDefaultProviderComplianceFrameworks("ory"));
  const [showAttestationGate, setShowAttestationGate] = useState(false);
  const [pendingSaveResult, setPendingSaveResult] = useState(null);

  useEffect(() => {
    if (!isEdit || !projectId) return;
    let cancelled = false;
    setInventoryBusy(true);
    setError(null);
    void (async () => {
      try {
        const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/ory-self-hosted-inputs`, {
          credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.ok === false) {
          throw new Error(data.error || "Failed to load project inputs");
        }
        if (cancelled) return;
        const inv = data.inventory || {};
        setInventory(inv);
        setKratosUrl(inv.kratos_admin_url || "");
        setHydraUrl(inv.hydra_admin_url || "");
        setAuthType(inv.auth_type || "none");
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load project inputs");
      } finally {
        if (!cancelled) setInventoryBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, projectId]);

  const configOnFile = (slotId) => {
    if (!inventory) return false;
    if (configRemoves[slotId]) return false;
    return Boolean(inventory.modules_on_file?.[slotId]);
  };

  const supplementalOnFile = (slotId) => {
    if (!inventory) return false;
    if (supplementalRemoves[slotId]) return false;
    if (slotId === "opl_upload") return Boolean(inventory.opl_present);
    if (slotId === "tuple_sample") return Boolean(inventory.tuple_aggregates_present);
    if (slotId === "access_rules_upload") return Boolean(inventory.access_rules_present);
    if (slotId === "sso_connections_upload") return Boolean(inventory.sso_connections_present);
    if (slotId === "dsync_upload") return Boolean(inventory.dsync_present);
    return false;
  };

  const hasNewConfigUpload = () =>
    Object.values(configUploads).some((value) => String(value || "").trim().length > 0) ||
    Object.values(supplementalUploads).some((value) => String(value || "").trim().length > 0);

  const hasConfigUpload = () => {
    if (hasNewConfigUpload()) return true;
    if (!isEdit || !inventory) {
      return Object.values(configUploads).some((value) => String(value || "").trim().length > 0) ||
        Object.values(supplementalUploads).some((value) => String(value || "").trim().length > 0);
    }
    const modulesOnFile = inventory.modules_on_file || {};
    const keptModules = CONFIG_UPLOAD_SLOTS.some((s) => modulesOnFile[s.id] && !configRemoves[s.id]);
    const keptOpl = inventory.opl_present && !supplementalRemoves.opl_upload;
    const keptTuples = inventory.tuple_aggregates_present && !supplementalRemoves.tuple_sample;
    const keptRules = inventory.access_rules_present && !supplementalRemoves.access_rules_upload;
    const keptSso = inventory.sso_connections_present && !supplementalRemoves.sso_connections_upload;
    const keptDsync = inventory.dsync_present && !supplementalRemoves.dsync_upload;
    return keptModules || keptOpl || keptTuples || keptRules || keptSso || keptDsync;
  };

  const payload = () => {
    const uploads = {};
    for (const slot of CONFIG_UPLOAD_SLOTS) {
      const raw = String(configUploads[slot.id] || "").trim();
      if (raw) uploads[slot.id] = raw;
    }
    const config_uploads_remove = {};
    for (const slot of CONFIG_UPLOAD_SLOTS) {
      if (configRemoves[slot.id]) config_uploads_remove[slot.id] = true;
    }
    const opl = String(supplementalUploads.opl_upload || "").trim();
    const tuples = String(supplementalUploads.tuple_sample || "").trim();
    const accessRules = String(supplementalUploads.access_rules_upload || "").trim();
    const ssoConnections = String(supplementalUploads.sso_connections_upload || "").trim();
    const dsync = String(supplementalUploads.dsync_upload || "").trim();
    const body = {
      deployment_mode: "self_hosted",
      kratos_admin_url: kratosUrl.trim() || undefined,
      hydra_admin_url: hydraUrl.trim() || undefined,
      auth_type: authType,
      config_uploads: Object.keys(uploads).length ? uploads : undefined,
      config_uploads_remove: Object.keys(config_uploads_remove).length ? config_uploads_remove : undefined,
      opl_upload: opl || undefined,
      tuple_sample: tuples || undefined,
      access_rules_upload: accessRules || undefined,
      sso_connections_upload: ssoConnections || undefined,
      dsync_upload: dsync || undefined,
      opl_upload_remove: supplementalRemoves.opl_upload || undefined,
      tuple_sample_remove: supplementalRemoves.tuple_sample || undefined,
      access_rules_upload_remove: supplementalRemoves.access_rules_upload || undefined,
      sso_connections_upload_remove: supplementalRemoves.sso_connections_upload || undefined,
      dsync_upload_remove: supplementalRemoves.dsync_upload || undefined,
    };
    if (bearerToken.trim()) body.bearer_token = bearerToken.trim();
    if (!isEdit) {
      body.display_name = displayName.trim() || undefined;
      body.complianceFrameworks = normalizeFrameworksForProviderSubmit("ory", frameworks);
    }
    if (isEdit && projectId) body.projectId = projectId;
    return body;
  };

  const canTest = () => hasConfigUpload() || kratosUrl.trim() || hydraUrl.trim();

  const handleTest = async () => {
    if (!canTest()) {
      setError("Upload at least one module config (JSON) or enter an admin URL to test.");
      return;
    }
    setError(null);
    setTestOk(false);
    setComponents(null);
    setTestBusy(true);
    try {
      const res = await fetch("/api/idp/ory/test-connection", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        setError(data.error || "Test connection failed");
        return;
      }
      setTestOk(true);
      setComponents(data.components || null);
    } catch (e) {
      setError(e?.message || "Test connection failed");
    } finally {
      setTestBusy(false);
    }
  };

  const finishSave = (result) => {
    if (isEdit) {
      onSaved?.(result);
    } else if (result?.projectId) {
      try {
        sessionStorage.setItem("idwiz.activeProjectId", result.projectId);
      } catch {
        /* ignore */
      }
      onConnected?.(result.projectId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!testOk) return;
    setError(null);
    setLoading(true);
    try {
      if (isEdit) {
        const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/ory-self-hosted-inputs`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload()),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.ok === false) {
          throw new Error(data.error || "Failed to update inputs");
        }
        if (data.requires_re_attestation || data.attestation_stale) {
          setPendingSaveResult(data);
          setShowAttestationGate(true);
          return;
        }
        finishSave(data);
      } else {
        const res = await fetch("/api/projects/ory-self-hosted", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload()),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.project?.projectId) {
          throw new Error(data.error || "Failed to create project");
        }
        finishSave({ projectId: data.project.projectId });
      }
    } catch (e) {
      setError(e?.message || (isEdit ? "Failed to update inputs" : "Failed to create project"));
    } finally {
      setLoading(false);
    }
  };

  if (inventoryBusy) {
    return (
      <div style={{ padding: compact ? "16px 24px" : "20px 32px", color: "var(--text-muted)", fontSize: 13 }}>
        Loading project configuration…
      </div>
    );
  }

  return (
    <>
      <form onSubmit={(e) => void handleSubmit(e)} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: compact ? "16px 24px 8px" : "20px 32px 8px" }}>
          <p className="text-secondary" style={{ fontSize: "12px", marginBottom: "16px", whiteSpace: "pre-line" }}>
            {isEdit
              ? "Update module configs, OPL, tuple samples, access rules, or admin URLs. Existing secrets on file are not shown — upload replacements or clear per module."
              : ORY_SELF_HOSTED_BODY_GUIDANCE}
          </p>

          {CONFIG_UPLOAD_SLOTS.map((slot) => (
            <details key={slot.id} open={Boolean(configUploads[slot.id]) || configOnFile(slot.id)} style={{ marginBottom: "12px" }}>
              <summary style={{ cursor: "pointer", fontSize: "13px", marginBottom: "8px", color: "var(--text-secondary)" }}>
                {slot.label.split(" — ")[0]}
                {configOnFile(slot.id) ? (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 11,
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: "color-mix(in srgb, var(--healthy) 15%, transparent)",
                      color: "var(--healthy)",
                    }}
                  >
                    Config on file
                    {inventory?.uploaded_config_summaries?.[slot.id]?.keys_retained
                      ? ` · ${inventory.uploaded_config_summaries[slot.id].keys_retained} keys`
                      : ""}
                  </span>
                ) : null}
              </summary>
              {configOnFile(slot.id) && !configUploads[slot.id] ? (
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={configRemoves[slot.id]}
                    onChange={(e) => {
                      setConfigRemoves((prev) => ({ ...prev, [slot.id]: e.target.checked }));
                      setTestOk(false);
                    }}
                  />
                  Remove stored {slot.id} config
                </label>
              ) : null}
              <label style={{ display: "block" }}>
                <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
                  {configOnFile(slot.id) ? `Replace ${slot.id} config (optional)` : `${slot.label} (optional)`}
                </span>
                <textarea
                  value={configUploads[slot.id]}
                  onChange={(e) => {
                    setConfigUploads((prev) => ({ ...prev, [slot.id]: e.target.value }));
                    setTestOk(false);
                  }}
                  rows={5}
                  placeholder={slot.placeholder}
                  style={{ ...fieldStyle, width: "100%", fontFamily: "monospace", fontSize: "11px" }}
                />
              </label>
            </details>
          ))}

          {ALL_SUPPLEMENTAL_UPLOAD_SLOTS.map((slot) => (
            <details key={slot.id} open={Boolean(supplementalUploads[slot.id]) || supplementalOnFile(slot.id)} style={{ marginBottom: "12px" }}>
              <summary style={{ cursor: "pointer", fontSize: "13px", marginBottom: "8px", color: "var(--text-secondary)" }}>
                {slot.label.split(" — ")[0]}
                {supplementalOnFile(slot.id) ? (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 11,
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: "color-mix(in srgb, var(--healthy) 15%, transparent)",
                      color: "var(--healthy)",
                    }}
                  >
                    {slot.id === "opl_upload"
                      ? "OPL on file"
                      : slot.id === "tuple_sample"
                        ? "Tuple aggregates on file"
                        : slot.id === "access_rules_upload"
                          ? "Access rules on file"
                          : slot.id === "sso_connections_upload"
                            ? "SSO aggregates on file"
                            : "SCIM/dsync aggregates on file"}
                  </span>
                ) : null}
              </summary>
              {supplementalOnFile(slot.id) && !supplementalUploads[slot.id] ? (
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={supplementalRemoves[slot.id]}
                    onChange={(e) => {
                      setSupplementalRemoves((prev) => ({ ...prev, [slot.id]: e.target.checked }));
                      setTestOk(false);
                    }}
                  />
                  Remove stored{" "}
                  {slot.id === "opl_upload"
                    ? "OPL"
                    : slot.id === "tuple_sample"
                      ? "tuple sample aggregates"
                      : slot.id === "access_rules_upload"
                        ? "access rules"
                        : slot.id === "sso_connections_upload"
                          ? "SSO connection aggregates"
                          : "SCIM/dsync aggregates"}
                </label>
              ) : null}
              <label style={{ display: "block" }}>
                <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
                  {supplementalOnFile(slot.id)
                    ? `Replace ${
                        slot.id === "opl_upload"
                          ? "OPL"
                          : slot.id === "tuple_sample"
                            ? "tuple sample"
                            : slot.id === "access_rules_upload"
                              ? "access rules"
                              : slot.id === "sso_connections_upload"
                                ? "SSO connections export"
                                : "SCIM/dsync export"
                      } (optional)`
                    : `${slot.label} (optional)`}
                </span>
                <textarea
                  value={supplementalUploads[slot.id]}
                  onChange={(e) => {
                    setSupplementalUploads((prev) => ({ ...prev, [slot.id]: e.target.value }));
                    setTestOk(false);
                  }}
                  rows={slot.rows || 5}
                  placeholder={slot.placeholder}
                  style={{ ...fieldStyle, width: "100%", fontFamily: "monospace", fontSize: "11px" }}
                />
              </label>
            </details>
          ))}

          <details open={Boolean(kratosUrl || hydraUrl || authType !== "none")} style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "14px" }}>
            <summary style={{ cursor: "pointer", marginBottom: "8px" }}>Live admin URLs (internal / dog-food only)</summary>
            <div style={{ display: "grid", gap: "12px", paddingTop: "8px" }}>
              <label style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
                <span className="text-secondary">Kratos admin URL (optional)</span>
                <input
                  type="url"
                  value={kratosUrl}
                  onChange={(e) => {
                    setKratosUrl(e.target.value);
                    setTestOk(false);
                  }}
                  placeholder="http://kratos.example.com:4434"
                  style={fieldStyle}
                />
              </label>

              <label style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
                <span className="text-secondary">Hydra admin URL (optional)</span>
                <input
                  type="url"
                  value={hydraUrl}
                  onChange={(e) => {
                    setHydraUrl(e.target.value);
                    setTestOk(false);
                  }}
                  placeholder="http://hydra.example.com:4445"
                  style={fieldStyle}
                />
              </label>

              <label style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
                <span className="text-secondary">Admin API auth</span>
                <select
                  value={authType}
                  onChange={(e) => {
                    setAuthType(e.target.value);
                    setTestOk(false);
                  }}
                  style={fieldStyle}
                >
                  <option value="none">None (open internal port)</option>
                  <option value="bearer">Bearer token</option>
                </select>
              </label>

              {authType === "bearer" ? (
                <label style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
                  <span className="text-secondary">
                    Bearer token{isEdit ? " (leave blank to keep existing)" : ""}
                  </span>
                  <input
                    type="password"
                    value={bearerToken}
                    onChange={(e) => {
                      setBearerToken(e.target.value);
                      setTestOk(false);
                    }}
                    autoComplete="off"
                    style={fieldStyle}
                  />
                </label>
              ) : null}
            </div>
          </details>

          {!isEdit ? (
            <>
              <label style={{ display: "block", marginBottom: "14px" }}>
                <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
                  Display name (optional)
                </span>
                <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={fieldStyle} />
              </label>

              <div style={{ marginBottom: "16px" }}>
                <ProviderFrameworkPicker providerId="ory" value={frameworks} onChange={setFrameworks} />
              </div>
            </>
          ) : null}

          {testOk ? (
            <p style={{ fontSize: "13px", color: "var(--status-ok, #4ade80)", marginBottom: "8px" }}>
              ✓ Ready to {isEdit ? "save" : "connect"}
              {components
                ? ` — Kratos: ${components.kratos || "?"}, Hydra: ${components.hydra || "?"}, Keto: ${components.keto || "?"}, Oathkeeper: ${components.oathkeeper || "?"}, Polis: ${components.polis || "?"}`
                : hasConfigUpload()
                  ? " (config upload — live identity/client data will be NOT_VISIBLE until admin API is connected)"
                  : ""}
            </p>
          ) : null}

          {error ? (
            <p className="text-critical" style={{ fontSize: "13px" }}>
              {error}
            </p>
          ) : null}
        </div>

        <div style={footerStyle}>
          {!testOk && !testBusy ? (
            <span className="text-secondary" style={{ fontSize: "11px", marginRight: "auto" }}>
              Validate config or admin URL before {isEdit ? "saving" : "creating the project"}.
            </span>
          ) : null}
          {onBack ? (
            <Button type="button" variant="ghost" onClick={onBack} disabled={loading || testBusy}>
              {isEdit ? "Cancel" : "Back"}
            </Button>
          ) : null}
          <Button type="button" variant="secondary" disabled={testBusy || loading || !canTest()} onClick={() => void handleTest()}>
            {testBusy ? "Validating…" : "Validate"}
          </Button>
          <Button type="submit" variant="primary" loading={loading} disabled={loading || !testOk}>
            {loading ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save inputs" : "Create project"}
          </Button>
        </div>
      </form>

      {showAttestationGate && projectId ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 210,
            background: "rgba(5, 8, 16, 0.72)",
            display: "grid",
            placeItems: "center",
            padding: 24,
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            style={{
              width: "100%",
              maxWidth: 640,
              maxHeight: "90vh",
              overflow: "auto",
              background: "var(--bg-surface)",
              border: "1px solid var(--border)",
              borderRadius: 10,
              padding: 24,
            }}
          >
            <OryAttestationGate
              projectId={projectId}
              onComplete={() => {
                setShowAttestationGate(false);
                finishSave(pendingSaveResult || {});
              }}
              onCancel={() => {
                setShowAttestationGate(false);
                finishSave(pendingSaveResult || {});
              }}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}

const fieldStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "4px",
  border: "1px solid var(--border)",
  color: "var(--text-primary)",
  background: "var(--bg-input)",
  fontSize: "14px",
};

const footerStyle = {
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "12px",
  padding: "16px 32px 24px",
  borderTop: "1px solid var(--border)",
};
